import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  TextField,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";

const API_URL =
  "https://ritehomepros.com/marketing_leads_backend/enquiry/create/";
const BUSINESS_ID = "e3896a07-6db2-4e91-a48f-9a25a9d07e9d";

const ContactFormSection = () => {
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    fname: "",
    lname: "Customer",
    email: "",
    mobile: "",
    service: "",
    message: "",
  });

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ================= VALIDATION ================= */
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateForm = () => {
    if (!form.service) return "Please select a service";
    if (!form.fname.trim()) return "Name is required";
    if (!emailRegex.test(form.email)) return "Enter a valid email";
    if (form.mobile.length !== 10)
      return "Enter a valid 10-digit phone number";
    return null;
  };

  /* ================= HANDLERS ================= */
  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    handleChange("mobile", value);
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      setSnack({ open: true, message: error, severity: "error" });
      return;
    }

    try {
      setLoading(true);

      await axios.post(API_URL, {
        business: BUSINESS_ID,
        fname: form.fname,
        lname: form.lname,
        mobile: form.mobile,
        email: form.email,
        source: "website",
        service: form.service,
        message: form.message,
      });

      setSnack({
        open: true,
        message: "Your message has been sent successfully!",
        severity: "success",
      });

      setForm({
        fname: "",
        lname: "Customer",
        email: "",
        mobile: "",
        service: "",
        message: "",
      });
    } catch (err) {
      setSnack({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .contact-form-section {
          background: #ffffff;
          padding: 80px 0;
        }

        .contact-card {
          background: #ffffff;
          border-radius: 26px;
          padding: 50px;
          box-shadow: 0 30px 80px rgba(15,23,42,0.08);
        }

        .form-title {
          font-size: 30px;
          font-weight: 800;
          margin-bottom: 10px;
          color: #111827;
        }

        .form-sub {
          font-size: 15px;
          color: #6b7280;
          margin-bottom: 32px;
        }

        .radio-wrap {
          display: flex;
          justify-content: center;
          gap: 18px;
          flex-wrap: wrap;
          margin-bottom: 40px;
        }

        .submit-btn {
          background: #7ac142;
          color: #fff;
          font-weight: 800;
          border-radius: 999px;
          padding: 14px 46px;
          box-shadow: 0 18px 40px rgba(122,193,66,.45);
        }

        .submit-btn:hover {
          background: #68aa37;
        }

        @media (max-width: 900px) {
          .contact-card {
            padding: 32px 24px;
          }
          .form-title {
            font-size: 24px;
          }
        }
      `}</style>

      <Box className="contact-form-section">
        <Container maxWidth="lg">
          <Box className="contact-card">
            <Typography align="center" className="form-title">
              Get In Touch
            </Typography>
            <Typography align="center" className="form-sub">
              Tell us what service you’re looking for and we’ll contact you
            </Typography>

            {/* ================= SERVICE SELECTION ================= */}
            <Typography align="center" fontWeight={700} mb={2}>
              What service are you looking for?
            </Typography>

            <RadioGroup
              row
              value={form.service}
              onChange={(e) =>
                handleChange("service", e.target.value)
              }
              className="radio-wrap"
            >
              {[
                "Water Mitigation & Restoration",
                "HVAC Services",
                "Cleaning Services",
                "Roofing Services",
              ].map((service) => (
                <FormControlLabel
                  key={service}
                  value={service}
                  control={<Radio color="success" />}
                  label={service}
                />
              ))}
            </RadioGroup>

            {/* ================= USER FORM ================= */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Your Name"
                  value={form.fname}
                  onChange={(e) =>
                    handleChange("fname", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Email Address"
                  value={form.email}
                  onChange={(e) =>
                    handleChange("email", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Phone Number"
                  value={form.mobile}
                  onChange={handlePhoneChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Your Message (optional)"
                  value={form.message}
                  onChange={(e) =>
                    handleChange("message", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} textAlign="center" mt={3}>
                <Button
                  className="submit-btn"
                  disabled={loading}
                  onClick={handleSubmit}
                >
                  {loading ? "Submitting..." : "Submit Message"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ================= SNACKBAR ================= */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnack({ ...snack, open: false })
        }
      >
        <Alert
          severity={snack.severity}
          onClose={() =>
            setSnack({ ...snack, open: false })
          }
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactFormSection;
